// Mother JRPG dialog (monorail-x76) — replaces the old world-anchored speech
// bubble (ui/dialog.ts) with a screen-fixed bottom box. Two modes:
//   • ambient ('greet') — typewriter line that auto-dismisses; sim keeps running
//   • beat            — freeze pages the player advances with menuConfirm
//
// The reducer is DOM-thin: all state lives in plain locals, and every DOM write
// funnels through render(), guarded so the pure state machine is unit-testable
// under vitest's node env (no document). Priority is fixed: beat > ambient.
//
// Narrator mutual exclusion (load-bearing): showing the box flags the narrator
// so its caption yields the shared bottom slot; hiding clears the flag.

import { setShopkeeperSpeaking } from '../engine/narrator'
import { playMotherLine } from '../engine/mother-audio'
import { motherPortraitSvg } from './portraits'
import { GLYPHS, type GameActions } from '../game/controls'
import type { DeviceKind } from '../engine/input'

const REVEAL_CHARS_PER_SEC = 45
const AMBIENT_DEFAULT_MS = 3500

type Mode = 'hidden' | 'ambient' | 'beat'

export interface MotherDialog {
  /** Ambient line: typewriter reveal, auto-dismiss after durationMs. */
  greet(line: string, durationMs?: number): void
  /** Freeze pages advanced by menuConfirm; onDone fires past the last page. */
  beat(pages: string[], onDone?: () => void): void
  /** True only in beat mode — drives main.ts's freeze gate. */
  isBeatActive(): boolean
  /** Advance the typewriter; in beat mode, consume menuConfirm to advance. */
  tick(actions: GameActions, dtMs: number): void
  /** Swap the advance-prompt glyph to match the active device (Phase F). */
  setDevice(d: DeviceKind): void
  hide(): void
}

interface Els {
  root: HTMLElement
  text: HTMLElement
  caret: HTMLElement
  portrait: HTMLElement
}

function grabEls(): Els | null {
  if (typeof document === 'undefined') return null
  const root = document.getElementById('mother-dialog')
  const text = document.getElementById('mother-dialog-text')
  const caret = document.getElementById('mother-dialog-caret')
  const portrait = document.getElementById('mother-dialog-portrait')
  if (!root || !text || !caret || !portrait) return null
  return { root, text, caret, portrait }
}

export function createMotherDialog(): MotherDialog {
  let mode: Mode = 'hidden'
  let pages: string[] = []
  let pageIndex = 0
  let revealChars = 0 // fractional; visible = floor(revealChars)
  let dismissMs = AMBIENT_DEFAULT_MS // ambient auto-dismiss countdown
  let onDone: (() => void) | undefined

  let els: Els | null = null
  let portraitDrawn = false
  let device: DeviceKind = 'kbm'

  const currentPage = (): string => pages[pageIndex] ?? ''
  const fullyRevealed = (): boolean => revealChars >= currentPage().length

  function render(): void {
    els ??= grabEls()
    if (!els) return
    if (!portraitDrawn) {
      els.portrait.innerHTML = motherPortraitSvg()
      portraitDrawn = true
    }
    if (mode === 'hidden') {
      els.root.classList.remove('visible')
      els.caret.style.display = 'none'
      return
    }
    els.root.classList.add('visible')
    els.text.textContent = currentPage().slice(0, Math.floor(revealChars))
    // Advance prompt shows only in beat mode once the page is fully revealed —
    // an explicit device-aware "what do I press" glyph, not just a caret.
    els.caret.textContent = `[${GLYPHS.menuConfirm[device]}] ▾`
    els.caret.style.display = mode === 'beat' && fullyRevealed() ? 'inline-block' : 'none'
  }

  function finish(): void {
    const cb = onDone
    hide()
    cb?.()
  }

  function hide(): void {
    mode = 'hidden'
    pages = []
    pageIndex = 0
    revealChars = 0
    onDone = undefined
    setShopkeeperSpeaking(false)
    render()
  }

  return {
    greet(line, durationMs = AMBIENT_DEFAULT_MS) {
      // Priority: a live beat owns the box — an ambient greet is dropped.
      if (mode === 'beat') return
      mode = 'ambient'
      pages = [line]
      pageIndex = 0
      revealChars = 0
      dismissMs = durationMs
      onDone = undefined
      setShopkeeperSpeaking(true)
      playMotherLine(line)
      render()
    },

    beat(newPages, cb) {
      // Beat outranks ambient and replaces a running beat cleanly (new onDone
      // wins; the old one is discarded, never called).
      setShopkeeperSpeaking(true)
      pages = newPages
      pageIndex = 0
      revealChars = 0
      onDone = cb
      if (pages.length === 0) {
        finish()
        return
      }
      mode = 'beat'
      playMotherLine(currentPage())
      render()
    },

    isBeatActive() {
      return mode === 'beat'
    },

    setDevice(d) {
      device = d
      render()
    },

    tick(actions, dtMs) {
      if (mode === 'hidden') return

      // Typewriter reveal.
      if (!fullyRevealed()) {
        revealChars = Math.min(
          currentPage().length,
          revealChars + (REVEAL_CHARS_PER_SEC * dtMs) / 1000,
        )
        // TODO(blip): hook a per-character typewriter tick sound here once the
        // audio SfxName union can take a new entry (cut deliberately for now).
      }

      if (mode === 'ambient') {
        if (fullyRevealed()) {
          dismissMs -= dtMs
          if (dismissMs <= 0) {
            hide()
            return
          }
        }
      } else if (mode === 'beat') {
        if (actions.justPressed('menuConfirm')) {
          actions.consume('menuConfirm') // one press = one advance, no leaks
          if (!fullyRevealed()) {
            revealChars = currentPage().length // JRPG instant-complete
          } else {
            pageIndex += 1
            if (pageIndex >= pages.length) {
              finish()
              return
            }
            revealChars = 0
            playMotherLine(currentPage())
          }
        }
      }

      render()
    },

    hide,
  }
}
